import { useCaseModuleModalRootActionsStore } from '@/features/cases/_shared/components/case-modal-root/case-modal-root.store';
import { memo, useCallback, useMemo } from 'react';
import { Modal, ModalHeader, ModalTitle, ModalDescription, ModalContent } from '@/components/_ui/modal';
import { handleDomBubbleEvent } from '@/utils/dom';
import { GET_REPLY_TEMPLATES_QUERY_KEY, useReplyTemplatesQuery } from '../../../api/get-reply-templates.api';
import { TReplyTemplate } from '../../../types/case.type';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { queryClient } from '@/lib/react-query';
import { PlusIcon } from 'lucide-react';
import { Button } from '@/components/_ui/button';

export type TSelectReplyTemplateModalType = 'SelectReplyTemplateModal';
export interface ISelectReplyTemplateModalProps {
  onSelect?: (template: TReplyTemplate) => void;
}

const SelectReplyTemplateModal = memo(({ onSelect }: ISelectReplyTemplateModalProps) => {
  const caseModuleModalRootActions = useCaseModuleModalRootActionsStore();

  const isOpenSelectReplyTemplateModal = useMemo(() => {
    return caseModuleModalRootActions.checkIsOpenModal('SelectReplyTemplateModal');
  }, [caseModuleModalRootActions]);

  const { data, isLoading } = useReplyTemplatesQuery({
    params: {
      page: 1,
      limit: 100,
      search: '',
      sortBy: 'createdAt',
      sortDir: 'DESC',
    },
    queryConfig: {
      enabled: isOpenSelectReplyTemplateModal,
    },
  });

  const displayTemplates = useMemo(() => data?.payload?.rows ?? [], [data?.payload?.rows]);

  const handleClose = useCallback(() => {
    caseModuleModalRootActions.closeModal('SelectReplyTemplateModal');
  }, [caseModuleModalRootActions]);

  const handleSelectTemplate = useCallback(
    (template: TReplyTemplate) => {
      onSelect?.(template);
      caseModuleModalRootActions.closeModal('SelectReplyTemplateModal');
    },
    [onSelect, caseModuleModalRootActions]
  );

  const handleOpenCreateReplyTemplateModal = useCallback(() => {
    caseModuleModalRootActions.openModal('CreateReplyTemplateModal', {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [GET_REPLY_TEMPLATES_QUERY_KEY] });
      },
    });
  }, [caseModuleModalRootActions]);

  return (
    <Modal isOpen={isOpenSelectReplyTemplateModal} lockBodyScroll onClose={handleClose} className="p-[32px] w-[500px]">
      <ModalHeader className="pr-[12px]">
        <div className="flex items-center justify-between gap-[8px]">
          <ModalTitle>Select Template</ModalTitle>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleOpenCreateReplyTemplateModal}
            startIcon={<PlusIcon className="size-4" />}
          >
            Add Template
          </Button>
        </div>
        <ModalDescription>Choose one template to insert into chat.</ModalDescription>
      </ModalHeader>
      <ModalContent>
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner isLoading className="w-8 h-8" />
          </div>
        ) : displayTemplates.length === 0 ? (
          <p className="py-6 text-center text-[14px] font-normal text-[#9D9D9C]">No templates available.</p>
        ) : (
          <div className="flex flex-col gap-[12px]">
            {displayTemplates.map((template) => (
              <button
                key={template.templateId}
                type="button"
                onClick={(e) => {
                  handleDomBubbleEvent(e);
                  handleSelectTemplate(template);
                }}
                className="w-full rounded-[8px] border border-[#D9D9D9] bg-white px-4 py-3 text-left transition-colors hover:border-[#0C8DBF] hover:bg-[#0C8DBF]/5"
              >
                <p className="text-[14px] font-semibold text-[#4B4B4B]">{template.title}</p>
                <p className="mt-1 line-clamp-2 text-[14px] font-normal text-[#828282]">{template.content || '—'}</p>
              </button>
            ))}
          </div>
        )}
      </ModalContent>
    </Modal>
  );
});
SelectReplyTemplateModal.displayName = 'SelectReplyTemplateModal';

export default SelectReplyTemplateModal;
